import { Injectable } from "@angular/core";
import { environment } from "../environment.ts/environment";

// Types and interfaces
export interface LoginResponse {
  success: boolean;
  message: string;
  data?: {
    user: AuthUser;
    token: string;
  };
}

export interface AuthUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  subscription: any;
}

export interface SubscriptionStatusResponse {
  success: boolean;
  message?: string;
  data: {
    clientExists: boolean;
    isSubscribed: boolean;
    client: any;
    subscription: any;
  };
}

// Constants
const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_USER_KEY = 'auth_user';

@Injectable({
    providedIn: 'root'
})
export class LoginService {

  async loginUser(email: string, password: string): Promise<LoginResponse> {
    try {
      // Validation des entrées
      if (!email) {
        return { success: false, message: 'Email requis' };
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return { success: false, message: 'Veuillez saisir une adresse email valide' };
      }

      // Vérifier le statut d'abonnement avec le mot de passe
      const subscriptionData = await this.checkSubscriptionStatus(email, password || '');
      
      if (!subscriptionData.success) {
        return { 
          success: false, 
          message: subscriptionData.message || 'Erreur lors de la vérification de votre statut' 
        };
      }

      const { clientExists, isSubscribed, client, subscription } = subscriptionData.data;
      
      // Vérifier si le client existe
      if (!clientExists) {
        return { 
          success: false, 
          message: 'Aucun compte trouvé avec cette adresse email. Veuillez vous inscrire d\'abord.' 
        };
      }
      
      // Vérifier si l'abonnement est actif
      if (!isSubscribed || subscription?.status !== 'ACTIF') {
        return { 
          success: false, 
          message: 'Votre abonnement n\'est pas actif. Veuillez renouveler votre abonnement pour accéder à votre espace client.' 
        };
      }

      // Créer l'objet utilisateur
      const user: AuthUser = {
        id: client.id,
        email: client.email,
        firstName: client.firstName,
        lastName: client.lastName,
        phone: client.phone,
        subscription: subscription
      };

      // Générer un token simple (dans un vrai projet, ce serait un JWT du serveur)
      const token = `pdftoword_${client.id}_${Date.now()}`;

      // Enregistrer dans le stockage local
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('user', JSON.stringify({
        ...user,
        loginTime: new Date().toISOString()
      }));

      return {
        success: true,
        message: 'Connexion réussie',
        data: {
          user,
          token
        }
      };
      
    } catch (error: any) {
      console.error('Erreur de connexion:', error);
      
      if (error.message) {
        return { success: false, message: error.message };
      }
      
      return { success: false, message: 'Une erreur est survenue. Veuillez réessayer.' };
    }
  }

  async checkSubscriptionStatus(email: string, password: string): Promise<SubscriptionStatusResponse> {
    try {
      const params = new URLSearchParams({
        email: email,
        id_site: '6'
      });

      // Ajouter le mot de passe s'il est fourni
      if (password && password.trim()) {
        params.append('password', password);
      }

      const response = await fetch(
        `${environment.apiBaseUrl}/clients/subscription-status?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erreur lors de la vérification du statut');
      }

      return data;
    } catch (error) {
      console.error('Erreur lors de la vérification du statut:', error);
      throw error;
    }
  }

  /**
   * Déconnecte l'utilisateur en supprimant tous les tokens et données du localStorage
   */
  logout(): void {
    // Supprimer tous les éléments du localStorage liés à l'authentification
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    
    console.log('Utilisateur déconnecté avec succès');
  }

  /**
   * Vérifie si l'utilisateur est connecté
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const isAuth = localStorage.getItem('isAuthenticated');
    return !!(token && isAuth === 'true');
  }

  /**
   * Récupère les informations de l'utilisateur connecté
   */
  getCurrentUser(): AuthUser | null {
    const userStr = localStorage.getItem(AUTH_USER_KEY);
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        console.error('Erreur lors du parsing des données utilisateur:', error);
        return null;
      }
    }
    return null;
  }

}